// Adding or editing one row by hand on a dataset's screen: a field for each of
// the dataset's fields (or one JSON field when it has none yet).
//
// Promise:
// - What is sent is what the server would take from a script: empty fields
//   are left out, numbers are numbers, JSON is parsed. A field that does not
//   parse, or a key field left empty, is said in the form and nothing is sent.
// - Editing a row of a dataset with a key: when the key's values change the
//   row is a new one, so the old one is removed in the same write. Without a
//   key the row keeps its `_id`.
// - The row shows on the screen through the copy's sync (one, the write's
//   own notice included).
import {
  type DatasetField,
  type DatasetRow,
  type DatasetSchema,
  type DatasetWriteOp,
  rowIdOf,
} from "@trameria/core/datasets-shared";
import { m } from "@trameria/i18n";
import { type FormEvent, useId, useState } from "react";
import { apiErrorMessage } from "../data/errors";
import { ErrorLine, ROW_INPUT, Switch } from "../ui/bits";
import { Button } from "../ui/button";
import { DateField } from "../ui/date-field";
import { useWriteDatasetRows } from "./queries";

type Values = Record<string, string | boolean>;

/** A field's value as the form holds it (text, or a check). */
function shown(field: DatasetField, value: unknown): string | boolean {
  if (field.type === "boolean") return value === true;
  if (value === null || value === undefined) return "";
  if (field.type === "json" || typeof value === "object")
    return JSON.stringify(value);
  return String(value);
}

/** The row to send, or the first problem with the form. */
export function rowOfForm(
  fields: readonly DatasetField[],
  key: readonly string[],
  values: Values,
): { row: Record<string, unknown> } | { error: string } {
  const row: Record<string, unknown> = {};
  for (const f of fields) {
    const v = values[f.name];
    if (f.type === "boolean") {
      row[f.name] = v === true;
      continue;
    }
    const text = typeof v === "string" ? v.trim() : "";
    if (text === "") {
      if (key.includes(f.name))
        return { error: m.datasets_key_missing({ field: f.name }) };
      continue;
    }
    if (f.type === "number") {
      const n = Number(text);
      if (!Number.isFinite(n))
        return { error: m.datasets_number_invalid({ field: f.name }) };
      row[f.name] = n;
    } else if (f.type === "json") {
      try {
        row[f.name] = JSON.parse(text);
      } catch {
        return { error: m.datasets_json_invalid({ field: f.name }) };
      }
    } else row[f.name] = text;
  }
  return { row };
}

/** The write for the form's row: added, or put in place of `editing`. */
export function opsOfForm(
  schema: DatasetSchema | null,
  row: Record<string, unknown>,
  editing: DatasetRow | null,
): DatasetWriteOp[] {
  if (!editing) return [{ op: "append", row }];
  const keyed = (schema?.key?.length ?? 0) > 0;
  if (!keyed) return [{ op: "upsert", row: { ...row, _id: editing._id } }];
  const id = rowIdOf(schema, row);
  return id === editing._id
    ? [{ op: "upsert", row }]
    : [
        { op: "remove", id: editing._id },
        { op: "upsert", row },
      ];
}

function FieldInput({
  field,
  value,
  onChange,
  timeZone,
}: {
  field: DatasetField;
  value: string | boolean;
  onChange(v: string | boolean): void;
  timeZone: string;
}) {
  const id = useId();
  const label = field.unit ? `${field.name} (${field.unit})` : field.name;
  const text = typeof value === "string" ? value : "";
  const control =
    field.type === "boolean" ? (
      <Switch checked={value === true} onChange={onChange} label={label} />
    ) : field.type === "date" ? (
      <DateField
        id={id}
        value={text}
        onChange={onChange}
        label={label}
        timeZone={timeZone}
        placeholder={m.datasets_date_pick()}
        clearLabel={m.datasets_date_clear()}
        className={`${ROW_INPUT} min-h-[34px] text-left`}
      />
    ) : field.type === "json" ? (
      <textarea
        id={id}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className={`${ROW_INPUT} h-auto py-1.5 font-mono`}
      />
    ) : (
      <input
        id={id}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        inputMode={field.type === "number" ? "decimal" : undefined}
        placeholder={
          field.type === "datetime" ? "2026-11-02T09:00:00+09:00" : undefined
        }
        className={ROW_INPUT}
      />
    );
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={field.type === "boolean" ? undefined : id}
        className="font-mono text-[11.5px] text-muted"
      >
        {label}
      </label>
      {control}
    </div>
  );
}

export function DatasetRowForm({
  datasetId,
  schema,
  fields,
  timeZone,
  editing,
  onDone,
}: {
  datasetId: string;
  /** The dataset's own schema (null: none; `fields` are then guessed). */
  schema: DatasetSchema | null;
  fields: readonly DatasetField[];
  timeZone: string;
  /** The row being edited, or null to add one. */
  editing: DatasetRow | null;
  onDone(): void;
}) {
  const write = useWriteDatasetRows(datasetId);
  const [values, setValues] = useState<Values>(() =>
    Object.fromEntries(
      fields.map((f) => [f.name, shown(f, editing?.[f.name])]),
    ),
  );
  // A dataset with no field yet takes the row as JSON.
  const [json, setJson] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const title = editing ? m.datasets_edit_row() : m.datasets_add_row();
  const key = schema?.key ?? [];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    let row: Record<string, unknown>;
    if (fields.length === 0) {
      try {
        const parsed: unknown = JSON.parse(json);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
          throw new Error("not an object");
        row = parsed as Record<string, unknown>;
      } catch {
        setProblem(m.datasets_row_json_invalid());
        return;
      }
    } else {
      const made = rowOfForm(fields, key, values);
      if ("error" in made) {
        setProblem(made.error);
        return;
      }
      row = made.row;
    }
    setProblem(null);
    write.mutate(
      { ops: opsOfForm(schema, row, editing) },
      { onSuccess: onDone },
    );
  };

  return (
    <form
      aria-label={title}
      onSubmit={submit}
      className="mt-3 rounded-[10px] border border-panel-border px-4 pt-3 pb-4"
    >
      <h3 className="text-[12.5px] text-fg">{title}</h3>
      <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
        {fields.length === 0 ? (
          <div className="col-span-full flex flex-col gap-1">
            <label
              htmlFor={`${datasetId}-json`}
              className="text-[11.5px] text-muted"
            >
              {m.datasets_row_json()}
            </label>
            <textarea
              id={`${datasetId}-json`}
              value={json}
              onChange={(e) => setJson(e.target.value)}
              rows={3}
              placeholder={'{"date": "2026-11-02", "count": 3}'}
              className={`${ROW_INPUT} h-auto py-1.5 font-mono`}
            />
          </div>
        ) : (
          fields.map((f) => (
            <FieldInput
              key={f.name}
              field={f}
              value={values[f.name] ?? ""}
              onChange={(v) => setValues((old) => ({ ...old, [f.name]: v }))}
              timeZone={timeZone}
            />
          ))
        )}
      </div>
      {(problem || write.isError) && (
        <div className="mt-3">
          <ErrorLine>
            {problem ?? apiErrorMessage(write.error, m.datasets_write_failed())}
          </ErrorLine>
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          {m.common_cancel()}
        </Button>
        <Button type="submit" variant="primary" disabled={write.isPending}>
          {m.common_save()}
        </Button>
      </div>
    </form>
  );
}
